/**
 * ReferenceSelector — Unified reference selector modal for canvas-v2.
 *
 * A centered viewport overlay with three media-type tabs (Image/Video/Audio),
 * an "On canvas" grid of selectable asset cards, and a summary row with
 * dnd-kit drag-to-reorder. Selection is saved explicitly via "Save & Close";
 * dismissing via X or Escape discards changes.
 *
 * Upload button (reference mode only): opens a flyout to upload Image/Video/Audio.
 * Creates a node on the canvas in the background. Shows a spinning card in the grid
 * while uploading; auto-selects on completion; removes card + shows toast on error.
 *
 * Replaces both ImagePickerModal and ReferenceImagePicker from V1.
 */

import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useNodes } from 'reactflow'
import { getThumbnailUrl, getLibraryThumbnailUrl, authenticatedFetch, getConfiguredApiBase, createNadDebug } from '@ponpon/api-client'
import { buildAssetCandidates, type CanvasAssetCandidate, type MediaType } from './mentionUtils'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { useLibraryYDoc } from '../../../../hooks/useLibraryYDoc'
import type { ReactFlowCanvasNode, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import { useCanvasOptional } from '../../CanvasContext'
import type { NodeDataRecord } from '../../nodeRegistry'
import { UploadFlyout } from '../../../shared/UploadFlyout'
import { getAcceptedReferenceMediaTypes } from './nodeReferenceCapabilities'
import { useWaveform } from './AudioRemixShared'
import { toast } from 'sonner'
export type { MediaType } from './mentionUtils'

const log = createNadDebug('dw:refselector:upload')
const previewLog = createNadDebug('dw:reference-selector:audio-preview')
const referenceSyncLog = createNadDebug('dw:reference-sync:selector')
const tabsLog = createNadDebug('dw:reference-tabs')

function referenceSyncTraceId(targetId: string): string {
  return `reference-sync:${targetId}`
}

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SelectedReference {
  nodeId: string
  nodeType: string
  label: string
  assetPath: string
  mediaType: MediaType
  source?: SourceTab
}

interface ImportReferenceResponse {
  node_type: string
  node_data: NodeDataRecord
  assets_copied: number
}

export interface ReferenceSelectorProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Callback to close the modal (without saving) */
  onClose: () => void
  /** Callback with selected references when user clicks Save & Close */
  onSave: (references: SelectedReference[]) => void
  /** The node that opened the modal (excluded from grid) */
  excludeNodeId: string
  /** All canvas nodes to scan for assets */
  canvasNodes: ReactFlowCanvasNode[]
  /** Current saved references (pre-populates the summary row) */
  currentReferences?: SelectedReference[]
  /** Maximum number of usable references (soft limit). Default: 4 */
  maxReferences?: number
  /** Restrict which media-type tabs are shown (default: all) */
  visibleTabs?: MediaType[]
  /**
   * Backend node type of the node opening the picker. When provided (and
   * `visibleTabs` is not), the picker hides media-type tabs the target node
   * cannot consume — e.g. an image_generator never sees a Video tab.
   * Derived from the generated schema field specs, so adding a video input
   * port to a schema automatically enables its Video tab.
   */
  targetBackendType?: string
  /** Operating mode: 'reference' for node input selection, 'asset-manager' for library browsing */
  mode?: 'reference' | 'asset-manager'
  /** Callback when user clicks "Add to canvas" in asset-manager mode */
  onAddToCanvas?: (references: SelectedReference[]) => void
  /** Callback to delete a node from the library (asset-manager mode) */
  onDeleteFromLibrary?: (nodeId: string) => void
  /** Upload callback: creates a node on the canvas and returns the node ID */
  onUploadFile?: (file: File, type: 'image' | 'video' | 'audio') => string
}

// ─── Tab definitions ─────────────────────────────────────────────────────────

/** Union of all tab keys: node-type tabs (library mode) + media-type tabs */
type TabKey = 'character' | 'location' | 'object' | MediaType

/** Mapping from node-type tab keys to the nodeType string used for filtering */
const NODE_TYPE_TAB_FILTER: Record<string, string> = {
  character: 'characterDesigner',
  location: 'locationDesigner',
  object: 'objectDesigner',
}

interface TabDef {
  key: TabKey
  label: string
  icon: React.ReactNode
}

export const IMAGE_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
)
export const VIDEO_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
)
export const AUDIO_ICON = (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2z" />
  </svg>
)

/** Media-type tabs — used in 'reference' mode */
const MEDIA_TABS: Array<TabDef & { key: MediaType }> = [
  { key: 'image', label: 'Image', icon: IMAGE_ICON },
  { key: 'video', label: 'Video', icon: VIDEO_ICON },
  { key: 'audio', label: 'Audio', icon: AUDIO_ICON },
]

/** Full tab set — used in 'asset-manager' mode (node-type tabs + media tabs) */
const LIBRARY_TABS: TabDef[] = [
  {
    key: 'character',
    label: 'Character',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
  {
    key: 'location',
    label: 'Location',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    key: 'object',
    label: 'Object',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
  },
  { key: 'image', label: 'Image', icon: IMAGE_ICON },
  { key: 'video', label: 'Video', icon: VIDEO_ICON },
  { key: 'audio', label: 'Audio', icon: AUDIO_ICON },
]

type SourceTab = 'canvas' | 'library'
type ReferenceCandidate = CanvasAssetCandidate & { source: SourceTab }
const SOURCE_TABS: SourceTab[] = ['canvas', 'library']

// ─── Pending upload info ─────────────────────────────────────────────────────

interface PendingUpload {
  nodeId: string
  mediaType: MediaType
  fileName: string
  previewUrl?: string
}

function referenceSource(ref: Pick<SelectedReference, 'source'>): SourceTab {
  return ref.source ?? 'canvas'
}

function selectedReferenceKey(ref: Pick<SelectedReference, 'nodeId' | 'mediaType' | 'source'>): string {
  return `${referenceSource(ref)}:${ref.nodeId}:${ref.mediaType}`
}

function withReferenceSource(candidate: CanvasAssetCandidate, source: SourceTab): ReferenceCandidate {
  return { ...candidate, source }
}

function summarizeReferences(refs: SelectedReference[]): string {
  return refs
    .slice(0, 12)
    .map((ref) => `${ref.nodeId}:${ref.mediaType}:${ref.assetPath}`)
    .join('|')
}

function resolveAssetPreviewUrl(projectId: string, assetPath: string, isLibrary?: boolean): string | null {
  return isLibrary
    ? getLibraryThumbnailUrl(assetPath)
    : getThumbnailUrl(projectId, assetPath, 0)
}

const AUDIO_GRID_BAR_COUNT = 48
const AUDIO_SUMMARY_BAR_COUNT = 20

function AudioReferenceWaveformPreview({
  url,
  barCount,
  className,
}: {
  url: string | null
  barCount: number
  className: string
}) {
  const bars = useWaveform(url, barCount)
  const renderedBars = bars.length > 0 ? bars : Array.from({ length: barCount }, () => 0.15)

  useEffect(() => {
    previewLog({
      event: 'audio-waveform-preview-render',
      hasUrl: Boolean(url),
      barCount,
      decodedBars: bars.length,
    })
  }, [url, barCount, bars.length])

  return (
    <div
      className={`${className} rounded-md bg-input-bg border border-border-subtle flex items-center gap-px px-2 py-1.5 overflow-hidden pointer-events-none`}
      data-testid="audio-reference-waveform-preview"
      aria-hidden="true"
    >
      {renderedBars.map((amp, i) => (
        <div
          key={i}
          data-testid="audio-reference-waveform-bar"
          className="flex-1 rounded-full bg-primary"
          style={{
            height: `${Math.max(12, amp * 100)}%`,
            opacity: bars.length > 0 ? 0.7 : 0.25,
          }}
        />
      ))}
    </div>
  )
}

// ─── SortableSummaryCard ─────────────────────────────────────────────────────

const SortableSummaryCard = memo(function SortableSummaryCard({
  reference,
  projectId,
  onRemove,
  isDimmed,
  isLibrary,
}: {
  reference: SelectedReference
  projectId: string
  onRemove: (key: string) => void
  isDimmed: boolean
  isLibrary?: boolean
}) {
  const sortableId = selectedReferenceKey(reference)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sortableId })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : isDimmed ? 0.35 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex flex-col items-center gap-1 p-1.5 rounded-lg border select-none cursor-grab active:cursor-grabbing w-20 flex-shrink-0 ${
        isDimmed
          ? 'border-white/5 bg-white/[0.02]'
          : 'border-white/10 bg-surface-1 hover:border-border'
      } ${isDragging ? 'shadow shadow-black/30' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Thumbnail */}
      {reference.mediaType === 'image' ? (
        <img
          src={resolveAssetPreviewUrl(projectId, reference.assetPath, isLibrary) ?? undefined}
          alt={reference.label}
          className="w-14 h-14 rounded object-cover bg-black/30"
        />
      ) : reference.mediaType === 'video' ? (
        <video
          src={resolveAssetPreviewUrl(projectId, reference.assetPath, isLibrary) ?? undefined}
          preload="metadata"
          muted
          className="w-14 h-[31.5px] rounded object-cover bg-black/30"
        />
      ) : (
        <AudioReferenceWaveformPreview
          url={resolveAssetPreviewUrl(projectId, reference.assetPath, isLibrary)}
          barCount={AUDIO_SUMMARY_BAR_COUNT}
          className="w-14 h-8"
        />
      )}

      {/* Label */}
      <span className="text-[10px] text-text-secondary truncate w-full text-center leading-tight">
        {reference.label}
      </span>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          onRemove(sortableId)
        }}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-surface-2 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80"
        title="Remove"
      >
        <svg className="w-2.5 h-2.5 text-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
})
SortableSummaryCard.displayName = 'SortableSummaryCard'

// ─── ReferenceSelector Component ─────────────────────────────────────────────

const ReferenceSelector = memo(function ReferenceSelector({
  isOpen,
  onClose,
  onSave,
  excludeNodeId,
  canvasNodes,
  currentReferences = [],
  maxReferences = 4,
  visibleTabs,
  targetBackendType,
  mode = 'reference',
  onAddToCanvas,
  onDeleteFromLibrary,
  onUploadFile,
}: ReferenceSelectorProps) {
  const projectId = useProjectId()
  const canvas = useCanvasOptional()
  const [sourceTab, setSourceTab] = useState<SourceTab>(mode === 'asset-manager' ? 'library' : 'canvas')
  const [isSaving, setIsSaving] = useState(false)

  // Effective tab restriction: explicit `visibleTabs` wins; otherwise derive
  // from the target node's accepted media types (schema-driven). Without this,
  // unsupported tabs would render and let users pick references that fail to
  // save (bug: Workplace post 1520084086415556).
  const effectiveVisibleTabs: MediaType[] | undefined = useMemo(() => {
    if (visibleTabs) return visibleTabs
    if (!targetBackendType) return undefined
    const derived = getAcceptedReferenceMediaTypes(targetBackendType)
    tabsLog('tabs-resolved %o', {
      targetBackendType,
      derivedTabs: derived,
      explicitVisibleTabs: null,
      finalTabs: derived,
    })
    return derived
  }, [visibleTabs, targetBackendType])

  // Determine tabs based on mode
  const activeTabs: TabDef[] = useMemo(() => {
    if (mode === 'asset-manager') return LIBRARY_TABS
    if (sourceTab === 'library' && !effectiveVisibleTabs) return LIBRARY_TABS
    return effectiveVisibleTabs
      ? MEDIA_TABS.filter((t) => effectiveVisibleTabs.includes(t.key))
      : MEDIA_TABS
  }, [mode, sourceTab, effectiveVisibleTabs])

  // Default tab: 'character' for asset-manager, first visible tab or 'image' for reference
  const defaultTab: TabKey = useMemo(
    () => mode === 'asset-manager' || (sourceTab === 'library' && !effectiveVisibleTabs)
      ? 'character'
      : (activeTabs[0]?.key ?? 'image'),
    [mode, sourceTab, effectiveVisibleTabs, activeTabs]
  )

  const [activeTab, setActiveTab] = useState<TabKey>(defaultTab)
  const [selected, setSelected] = useState<SelectedReference[]>([])
  const initializedRef = useRef(false)

  // Delete confirmation state
  const [pendingDeleteNodeId, setPendingDeleteNodeId] = useState<string | null>(null)

  // ── Pending uploads ────────────────────────────────────────────────────────
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([])

  // Get reactive nodes from ReactFlow when we have pending uploads.
  // useNodes() subscribes to ReactFlow store updates (which include YDoc sync).
  const reactiveNodes = useNodes<ReactFlowNodeData>()

  // Use reactive nodes when there are pending uploads, otherwise use the snapshot.
  const effectiveCanvasNodes = pendingUploads.length > 0 ? reactiveNodes : canvasNodes

  useEffect(() => {
    setSourceTab(mode === 'asset-manager' ? 'library' : 'canvas')
  }, [mode, isOpen])

  useEffect(() => {
    if (!activeTabs.some((tab) => tab.key === activeTab)) {
      setActiveTab(defaultTab)
    }
  }, [activeTab, activeTabs, defaultTab])

  // Connect to library YDoc only when the active source needs it.
  const { nodes: libraryNodes } = useLibraryYDoc({ enabled: sourceTab === 'library' })

  // Reset internal state when modal opens — refresh labels from current candidates.
  // Only reset when opening (not while open) to avoid wiping user selections.
  useEffect(() => {
    if (isOpen && !initializedRef.current) {
      // Refresh labels from current buildAssetCandidates output to avoid stale labels
      const candidateLookup = new Map(
        buildAssetCandidates(effectiveCanvasNodes, excludeNodeId)
          .map((c) => withReferenceSource(c, 'canvas'))
          .map((c): [string, ReferenceCandidate] => [selectedReferenceKey(c), c])
      )
      const refreshed = currentReferences.map((ref) => {
        const fresh = candidateLookup.get(selectedReferenceKey(ref))
        return fresh
          ? { ...ref, label: fresh.label, assetPath: fresh.assetPath, source: referenceSource(ref) }
          : ref
      })
      referenceSyncLog({
        event: 'selector-open',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        mode,
        sourceTab,
        currentRefCount: currentReferences.length,
        refreshedRefCount: refreshed.length,
        candidateCount: candidateLookup.size,
        currentRefs: summarizeReferences(currentReferences),
        refreshedRefs: summarizeReferences(refreshed),
      })
      setSelected(refreshed)
      setActiveTab(defaultTab)
      // Revoke blob URLs before clearing pending uploads to avoid memory leaks
      setPendingUploads((prev) => {
        for (const pu of prev) {
          if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
        }
        return []
      })
      initializedRef.current = true
    } else if (!isOpen) {
      initializedRef.current = false
    }
  }, [isOpen, currentReferences, effectiveCanvasNodes, excludeNodeId, visibleTabs, activeTabs, mode, sourceTab, libraryNodes, defaultTab])

  // Build asset candidates from the active source
  const allCandidates = useMemo(() => {
    const sourceNodes = sourceTab === 'library' ? libraryNodes : effectiveCanvasNodes
    const excludeId = sourceTab === 'library' ? undefined : excludeNodeId
    return buildAssetCandidates(sourceNodes, excludeId).map((candidate) => ({
      ...candidate,
      source: sourceTab,
    }))
  }, [sourceTab, libraryNodes, effectiveCanvasNodes, excludeNodeId])

  // ── Watch pending uploads for completion/error ─────────────────────────────
  useEffect(() => {
    if (pendingUploads.length === 0) return

    setPendingUploads((prev) => {
      const remaining: PendingUpload[] = []
      for (const pu of prev) {
        const node = effectiveCanvasNodes.find((n) => n.id === pu.nodeId)
        const nodeStatus = typeof node?.data?.status === 'string' ? node.data.status : undefined

        if (nodeStatus === 'completed') {
          // Check if buildAssetCandidates finds an asset for this node
          const asset = node ? buildAssetCandidates([node]).find((c) => c.nodeId === pu.nodeId) : undefined
          if (asset) {
            log({ event: 'upload-complete', nodeId: pu.nodeId, mediaType: pu.mediaType })
            // Revoke blob URL to avoid memory leak
            if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
            // Auto-select
            setSelected((sel) => {
              if (sel.some((r) => r.nodeId === pu.nodeId)) return sel
              return [
                ...sel,
                {
                  nodeId: asset.nodeId,
                  nodeType: asset.nodeType,
                  label: asset.label,
                  assetPath: asset.assetPath,
                  mediaType: asset.mediaType,
                  source: 'canvas',
                },
              ]
            })
            continue // remove from pending
          }
        }

        if (nodeStatus === 'error') {
          log({ event: 'upload-error', nodeId: pu.nodeId, mediaType: pu.mediaType })
          // Revoke blob URL to avoid memory leak
          if (pu.previewUrl) URL.revokeObjectURL(pu.previewUrl)
          // Remove from pending — the upload failed
          continue
        }

        remaining.push(pu)
      }
      return remaining
    })
  }, [pendingUploads, effectiveCanvasNodes])

  // ── Handle upload from flyout ──────────────────────────────────────────────
  const handleUpload = useCallback(
    (file: File, type: 'image' | 'video' | 'audio') => {
      if (!onUploadFile) return
      const nodeId = onUploadFile(file, type)
      log({ event: 'upload-start', nodeId, type, fileName: file.name })

      // Track as pending
      const previewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined
      setPendingUploads((prev) => [
        ...prev,
        { nodeId, mediaType: type, fileName: file.name, previewUrl },
      ])

      // Auto-switch tab to match uploaded type
      setActiveTab(type)
    },
    [onUploadFile],
  )

  const handleSourceTabChange = useCallback(
    (nextSource: SourceTab) => {
      setSourceTab(nextSource)
      if (!visibleTabs) {
        setActiveTab(nextSource === 'library' ? 'character' : 'image')
      }
      referenceSyncLog({
        event: 'source-change',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        mode,
        sourceTab: nextSource,
      })
    },
    [excludeNodeId, mode, visibleTabs],
  )

  // Filter candidates by active tab (node-type tabs filter by nodeType, media tabs by mediaType)
  const filteredCandidates = useMemo(
    () => allCandidates.filter((c) => {
      const nodeTypeFilter = NODE_TYPE_TAB_FILTER[activeTab]
      if (nodeTypeFilter) return c.nodeType === nodeTypeFilter
      return c.mediaType === activeTab
    }),
    [allCandidates, activeTab]
  )

  // Filter pending uploads by active tab
  const filteredPending = useMemo(
    () => pendingUploads.filter((pu) => pu.mediaType === activeTab),
    [pendingUploads, activeTab]
  )

  // Selected node IDs set for quick lookup
  const selectedKeys = useMemo(
    () => new Set(selected.map((r) => selectedReferenceKey(r))),
    [selected]
  )

  // Toggle selection
  const toggleSelection = useCallback((candidate: ReferenceCandidate) => {
    setSelected((prev) => {
      const candidateKey = selectedReferenceKey(candidate)
      const exists = prev.find((r) => selectedReferenceKey(r) === candidateKey)
      if (exists) {
        return prev.filter((r) => selectedReferenceKey(r) !== candidateKey)
      }
      return [
        ...prev,
        {
          nodeId: candidate.nodeId,
          nodeType: candidate.nodeType,
          label: candidate.label,
          assetPath: candidate.assetPath,
          mediaType: candidate.mediaType,
          source: candidate.source,
        },
      ]
    })
  }, [])

  // Remove from summary row
  const removeSelected = useCallback((key: string) => {
    setSelected((prev) => prev.filter((r) => selectedReferenceKey(r) !== key))
  }, [])

  // Drag-to-reorder in summary row
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  )

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setSelected((prev) => {
      const oldIndex = prev.findIndex((r) => selectedReferenceKey(r) === active.id)
      const newIndex = prev.findIndex((r) => selectedReferenceKey(r) === over.id)
      if (oldIndex === -1 || newIndex === -1) return prev
      return arrayMove(prev, oldIndex, newIndex)
    })
  }, [])

  const importLibraryReferences = useCallback(
    async (refs: SelectedReference[]): Promise<SelectedReference[]> => {
      const hasLibraryRefs = refs.some((ref) => referenceSource(ref) === 'library')
      if (!hasLibraryRefs) return refs
      if (!canvas) {
        throw new Error('Canvas context is unavailable for library import')
      }

      const targetNode = canvas.getNode(excludeNodeId)
      const basePosition = targetNode?.position ?? { x: 0, y: 0 }
      const apiBase = getConfiguredApiBase()
      let importIndex = 0

      referenceSyncLog({
        event: 'library-import-start',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        projectId,
        selectedCount: refs.length,
        libraryCount: refs.filter((ref) => referenceSource(ref) === 'library').length,
      })

      const importedByKey = new Map<string, SelectedReference>()
      for (const ref of refs) {
        if (referenceSource(ref) !== 'library') continue

        const resp = await authenticatedFetch(
          `${apiBase}/library/nodes/${ref.nodeId}/import`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ target_project_id: projectId }),
          },
        )
        if (!resp.ok) {
          const err = await resp.json().catch(() => null)
          throw new Error(err?.detail || resp.statusText)
        }

        const { node_type, node_data }: ImportReferenceResponse = await resp.json()
        const position = {
          x: basePosition.x - 260,
          y: basePosition.y + importIndex * 80,
        }
        const importedNodeId = canvas.addNode(node_type, position, node_data)
        const importedCandidate = buildAssetCandidates([
          {
            id: importedNodeId,
            type: node_type,
            data: node_data,
            position,
          },
        ]).find((candidate) => candidate.mediaType === ref.mediaType)

        importedByKey.set(selectedReferenceKey(ref), {
          ...ref,
          nodeId: importedNodeId,
          nodeType: node_type,
          label: importedCandidate?.label ?? ref.label,
          assetPath: importedCandidate?.assetPath ?? ref.assetPath,
          source: 'canvas',
        })
        importIndex += 1
      }

      const resolved = refs.map((ref) => importedByKey.get(selectedReferenceKey(ref)) ?? ref)
      referenceSyncLog({
        event: 'library-import-complete',
        trace_id: referenceSyncTraceId(excludeNodeId),
        targetId: excludeNodeId,
        projectId,
        importedCount: importedByKey.size,
        resolvedRefs: summarizeReferences(resolved),
      })
      return resolved
    },
    [canvas, excludeNodeId, projectId],
  )

  // Save: trim to max and call onSave
  const handleSave = useCallback(async () => {
    const trimmed = selected.slice(0, maxReferences)
    referenceSyncLog({
      event: 'selector-save',
      trace_id: referenceSyncTraceId(excludeNodeId),
      targetId: excludeNodeId,
      mode,
      selectedCount: selected.length,
      savedCount: trimmed.length,
      selectedRefs: summarizeReferences(selected),
      savedRefs: summarizeReferences(trimmed),
    })
    if (mode === 'asset-manager' && onAddToCanvas) {
      onAddToCanvas(trimmed)
    } else {
      setIsSaving(true)
      try {
        const resolved = await importLibraryReferences(trimmed)
        onSave(resolved)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : String(err)
        referenceSyncLog({
          event: 'library-import-failed',
          trace_id: referenceSyncTraceId(excludeNodeId),
          targetId: excludeNodeId,
          projectId,
          error: errorMessage,
        })
        toast.error(`Failed to add library reference: ${errorMessage}`)
      } finally {
        setIsSaving(false)
      }
    }
  }, [selected, maxReferences, onSave, mode, onAddToCanvas, excludeNodeId, importLibraryReferences, projectId])

  // Discard: close without saving
  const handleDiscard = useCallback(() => {
    onClose()
  }, [onClose])

  // Escape key handler
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        handleDiscard()
      }
    }
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [isOpen, handleDiscard])

  const isOverLimit = selected.length > maxReferences

  if (!isOpen) return null

  const showUploadButton = mode === 'reference' && !!onUploadFile

  const mainContent = createPortal(
    <div
      className="fixed inset-0 z-[9998] flex items-center justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleDiscard()
      }}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className="relative z-[9999] flex flex-col bg-background border border-border rounded-xl shadow-2xl"
        style={{ width: '60vw', maxWidth: 900, height: '70vh', maxHeight: 720 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="reference-selector-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <h2 className="text-sm font-semibold text-foreground" data-testid="reference-selector-title">
            {mode === 'asset-manager' ? 'Asset Selector' : 'Reference Selector'}
          </h2>
          <button
            onClick={handleDiscard}
            className="w-7 h-7 rounded-md flex items-center justify-center text-foreground/40 hover:text-foreground/80 hover:bg-white/5 transition-colors"
            data-testid="reference-selector-close"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1 px-5 py-2 border-b border-border/50">
          {mode === 'reference' && (
            <div
              className="flex items-center gap-1 rounded-md bg-surface-1 border border-border-subtle p-0.5 mr-2"
              data-testid="source-tab-bar"
            >
              {SOURCE_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => handleSourceTabChange(tab)}
                  className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                    sourceTab === tab
                      ? 'bg-surface-2 text-foreground'
                      : 'text-text-muted hover:text-foreground/70 hover:bg-surface-2'
                  }`}
                  data-testid={`source-tab-${tab}`}
                >
                  {tab === 'canvas' ? 'Canvas' : 'Library'}
                </button>
              ))}
            </div>
          )}
          {/* Tabs */}
          {activeTabs.map((tab) => {
            const nodeTypeFilter = NODE_TYPE_TAB_FILTER[tab.key]
            const count = nodeTypeFilter
              ? allCandidates.filter((c) => c.nodeType === nodeTypeFilter).length
              : allCandidates.filter((c) => c.mediaType === tab.key).length
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeTab === tab.key
                    ? 'bg-white/10 text-foreground'
                    : 'text-text-muted hover:text-foreground/70 hover:bg-white/5'
                }`}
                data-testid={`reference-tab-${tab.key}`}
              >
                {tab.icon}
                {tab.label}
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full bg-white/10 text-[10px] ${count === 0 ? 'invisible' : ''}`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        {/* Grid area */}
        <div className="flex-1 overflow-y-auto px-5 py-3 nowheel" onWheel={(e) => e.stopPropagation()}>
          <h3 className="text-xs font-medium text-text-muted mb-2">
            {sourceTab === 'library' ? 'In library' : 'On canvas'}
          </h3>

          {(filteredCandidates.length > 0 || filteredPending.length > 0) ? (
            <div className="grid grid-cols-4 gap-3" data-testid="reference-grid">
              {/* Pending upload cards at the top */}
              {filteredPending.map((pu) => (
                <div
                  key={`pending-${pu.nodeId}`}
                  className="relative flex flex-col items-center gap-1.5 p-2 rounded-lg border border-white/5 bg-surface-2/50 cursor-not-allowed opacity-70"
                  data-testid={`uploading-card-${pu.nodeId}`}
                >
                  {/* Spinner overlay */}
                  <div className="w-full aspect-square rounded-md bg-black/30 flex items-center justify-center relative overflow-hidden">
                    {pu.previewUrl && (
                      <img
                        src={pu.previewUrl}
                        alt={pu.fileName}
                        className="absolute inset-0 w-full h-full object-cover opacity-40"
                      />
                    )}
                    <div className="relative z-10">
                      <svg className="w-6 h-6 animate-spin text-foreground/50" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    </div>
                  </div>
                  <span className="text-xs text-text-secondary truncate w-full text-center">
                    {pu.fileName}
                  </span>
                </div>
              ))}

              {/* Normal candidate cards */}
              {filteredCandidates.map((candidate) => {
                const isSelected = selectedKeys.has(selectedReferenceKey(candidate))
                return (
                  <button
                    key={candidate.nodeId}
                    onClick={() => toggleSelection(candidate)}
                    className={`group relative flex flex-col items-center gap-1.5 p-2 rounded-lg transition-all border ${
                      isSelected
                        ? 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'
                        : 'border-white/5 hover:border-border hover:bg-surface-2'
                    }`}
                    data-testid={`reference-card-${candidate.nodeId}`}
                  >
                    {/* Checkmark overlay */}
                    {isSelected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}

                    {/* Delete from library button */}
                    {mode === 'asset-manager' && sourceTab === 'library' && !isSelected && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          setPendingDeleteNodeId(candidate.nodeId)
                        }}
                        className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-surface-2 border border-white/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80 z-10"
                        title="Remove from library"
                        data-testid={`reference-card-delete-${candidate.nodeId}`}
                      >
                        <svg className="w-3 h-3 text-foreground/70" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}

                    {/* Thumbnail */}
                    {candidate.mediaType === 'image' ? (
                      <img
                        src={resolveAssetPreviewUrl(projectId, candidate.assetPath, sourceTab === 'library') ?? undefined}
                        alt={candidate.label}
                        className="w-full aspect-square rounded-md object-cover bg-black/30"
                        loading="lazy"
                      />
                    ) : candidate.mediaType === 'video' ? (
                      <video
                        src={resolveAssetPreviewUrl(projectId, candidate.assetPath, sourceTab === 'library') ?? undefined}
                        preload="metadata"
                        muted
                        className="w-full aspect-video rounded-md object-cover bg-black/30"
                      />
                    ) : (
                      <AudioReferenceWaveformPreview
                        url={resolveAssetPreviewUrl(projectId, candidate.assetPath, sourceTab === 'library')}
                        barCount={AUDIO_GRID_BAR_COUNT}
                        className="w-full aspect-[2/1]"
                      />
                    )}

                    {/* Label */}
                    <span className="text-xs text-text-secondary truncate w-full text-center">
                      {candidate.label}
                    </span>
                  </button>
                )
              })}
            </div>
          ) : sourceTab === 'library' ? (
            <div className="py-12 text-center" data-testid="library-empty-state">
              <svg className="w-10 h-10 mx-auto text-foreground/10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              <p className="text-sm text-foreground/30">
                No saved assets. Use the bookmark button on any node to save it here.
              </p>
            </div>
          ) : (
            <div className="py-12 text-center">
              <svg className="w-10 h-10 mx-auto text-foreground/10 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-foreground/30" data-testid="reference-empty-state">
                No {activeTab === 'image' ? 'images' : activeTab === 'video' ? 'videos' : 'audio'} on canvas.
              </p>
            </div>
          )}

        </div>

        {/* Summary row */}
        <div className="border-t border-border px-5 py-3" data-testid="reference-summary">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-text-muted">
              Selected ({selected.length}/{maxReferences})
            </span>
            {isOverLimit && (
              <span className="text-[10px] text-yellow-400/90" data-testid="reference-limit-warning">
                Maximum {maxReferences} references — only the first {maxReferences} will be used.
              </span>
            )}
          </div>

          {selected.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={selected.map((r) => selectedReferenceKey(r))}
                strategy={horizontalListSortingStrategy}
              >
                <div className="flex gap-2 overflow-x-auto pb-1 nowheel" onWheel={(e) => e.stopPropagation()}>
                  {selected.map((ref, index) => (
                    <SortableSummaryCard
                      key={selectedReferenceKey(ref)}
                      reference={ref}
                      projectId={projectId}
                      onRemove={removeSelected}
                      isDimmed={index >= maxReferences}
                      isLibrary={referenceSource(ref) === 'library'}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <p className="text-xs text-foreground/20 py-3 text-center" data-testid="reference-no-selection">
              No references selected.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className={`flex items-center ${showUploadButton ? 'justify-between' : 'justify-end'} px-5 py-3 border-t border-border/50`}>
          {/* Upload button (reference mode only) */}
          {showUploadButton && (
            <UploadFlyout
              onUploadFile={handleUpload}
              size="sm"
              testIdPrefix="refselector-upload"
            />
          )}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm rounded-lg bg-primary/90 hover:bg-primary text-primary-foreground font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="reference-save-btn"
          >
            {isSaving ? 'Adding...' : mode === 'asset-manager' ? 'Add to canvas' : 'Save & Close'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )

  // Delete confirmation dialog (separate portal at higher z-index)
  const deleteDialog = pendingDeleteNodeId ? createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => { if (e.target === e.currentTarget) setPendingDeleteNodeId(null) }}
      data-testid="delete-confirmation-backdrop"
    >
      <div
        className="relative w-full max-w-md bg-surface-1 rounded-lg border border-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-confirmation-dialog"
      >
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-medium text-foreground">Delete Asset</h2>
        </div>
        <div className="p-5">
          <p className="text-sm text-muted-foreground mb-4">
            This will permanently delete the asset from your library. But don&apos;t worry, any projects that have already imported this asset will not be affected.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setPendingDeleteNodeId(null)}
              className="px-3 py-1.5 text-sm rounded-md border border-border hover:bg-surface-2 transition-colors"
              data-testid="delete-confirmation-cancel"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onDeleteFromLibrary?.(pendingDeleteNodeId)
                setSelected((prev) => prev.filter((r) => r.nodeId !== pendingDeleteNodeId))
                setPendingDeleteNodeId(null)
              }}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white transition-colors"
              data-testid="delete-confirmation-confirm"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null

  return (
    <>
      {mainContent}
      {deleteDialog}
    </>
  )
})

ReferenceSelector.displayName = 'ReferenceSelector'
export { ReferenceSelector }
export type { SelectedReference as ReferenceSelectorSelection }
